import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { captureNarrative } from '@/api/endpoints';

export interface VaultCapturePromptProps {
  questionText: string;
  answerText: string;
  category?: string | null;
  sessionId: string;
  responseId: string;
  onSave: () => void;
  onSkip: () => void;
}

type PromptState = 'prompt' | 'saving' | 'saved' | 'skipped';

export function VaultCapturePrompt({
  questionText,
  answerText,
  category,
  sessionId,
  responseId,
  onSave,
  onSkip,
}: VaultCapturePromptProps) {
  const [state, setState] = useState<PromptState>('prompt');

  const mutation = useMutation({
    mutationFn: () =>
      captureNarrative({
        question_text: questionText,
        answer_text: answerText,
        category: category || 'general',
        source_session_id: sessionId,
        source_response_id: responseId,
      }),
    onSuccess: () => {
      setState('saved');
      onSave();
    },
    onError: () => {
      setState('prompt');
    },
  });

  const handleSave = () => {
    setState('saving');
    mutation.mutate();
  };

  const handleSkip = () => {
    setState('skipped');
    onSkip();
  };

  if (state === 'skipped') {
    return null;
  }

  if (state === 'saved') {
    return (
      <Badge
        variant="outline"
        className="bg-green-50 text-green-700 border-green-200 text-xs"
      >
        Saved to vault
      </Badge>
    );
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center justify-between gap-4">
      <p className="text-sm text-blue-700">
        Save to vault for future DDQs?
      </p>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={handleSkip}
          disabled={state === 'saving'}
        >
          Skip
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={state === 'saving'}
        >
          {state === 'saving' ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
